<template>
  <teleport to="body">
    <pdl-drawer size="23rem" show-close :is-open="isDrawerOpen" :style="headerStyle" @close="handleClose">
      <template #content>
        <retailer-select-input
          :initial-value="geolocation.address"
          :handle-close="handleClose"
          :search="fetchInitialRetailers"
          @coordinates-from-input="handleLocationUpdate"
        />
        <pdl-loading :is-loading="isLoading">
          <div class="my-3 space-y-6 flex flex-col">
            <div v-if="!retailerList.length" class="text-center" qaid="retailer-drawer-no-results">
              {{ $t('text.search.noResults') }}
            </div>
            <template v-else>
              <p class="text-xs flex justify-center gap-x-1" qaid="retailer-pricing-note">
                <span class="font-bold"> {{ $t('tcrm.B2B.legal.3') }} </span>
                {{ $t('fulfillment.retailer.change.pricing') }}
              </p>
              <div class="flex flex-col" data-retailer-cards-container>
                <retailer-card
                  v-for="(retailer, i) in displayRetailerList"
                  :key="`retailer-card-${retailer.id || i}`"
                  :retailer="retailer"
                  :is-account-section="isAccountSection"
                  :is-selected="retailer.name === selectedRetailer?.name"
                  :set-selected-retailer="handleRetailerSelected"
                  :qaid="`retailer-card-${i}`"
                />
              </div>
            </template>
          </div>
          <pdl-loading ref="loadMoreTarget" :is-loading="isLoadingMore" />
        </pdl-loading>
      </template>
    </pdl-drawer>
  </teleport>
</template>

<script>
import {PdlDrawer} from '@pedal/pdl-drawer';
import {PdlLoading} from '@pedal/pdl-loading';
import RetailerSelectInput from '@/components/drawer/retailer/RetailerSelectInput.vue';
import RetailerCard from '@/components/drawer/retailer/RetailerCard.vue';
import RetailerApi from '@/api/retailer-api';
import {mapMutations, mapState, mapActions} from 'vuex';
import {convertPreferredUnitToMeters} from '@/utils/unit-converter';
import {UnitTypes} from '@/constants/unit-types';

const PAGE_SIZE = 15;
const RADIUS_IN_MILES = 200;

export default {
  components: {PdlDrawer, PdlLoading, RetailerSelectInput, RetailerCard},
  props: {
    isDrawerOpen: {
      type: Boolean,
      required: true,
    },
    isAccountSection: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      retailerList: [],
      currentPage: 0,
      totalPages: undefined,
      currentSearchCoordinates: {},
      isLoading: false,
      isLoadingMore: false,
      observer: null,
      coordinatesFromInput: [],
    };
  },
  computed: {
    ...mapState('backend', ['geolocation']),
    ...mapState('user', ['selectedRetailer', 'userPreferredRetailer']),
    ...mapState('productDetails', ['variantCode']),
    ...mapState('global', ['headerHeight']),
    ...mapState('cart', ['outOfStockProductSku']),

    headerStyle() {
      return {
        '--header-height': `${this.headerHeight}px`,
      };
    },

    displayRetailerList() {
      let sortedList = [...this.retailerList];

      // First, check if userPreferredRetailer exists and should be prioritized
      if (this.userPreferredRetailer && Object.keys(this.userPreferredRetailer).length && this.isAccountSection) {
        // Check if userPreferredRetailer is already in the list
        const preferredRetailerIndex = sortedList.findIndex(
          (retailer) => retailer.name === this.userPreferredRetailer.name
        );

        if (preferredRetailerIndex > -1) {
          const [preferredRetailer] = sortedList.splice(preferredRetailerIndex, 1);
          // Remove from current position and add to beginning
          sortedList.unshift(preferredRetailer);
        } else {
          // If not in list, add to beginning
          sortedList.unshift(this.userPreferredRetailer);
        }
      }
      return sortedList;
    },
  },
  watch: {
    variantCode(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchInitialRetailers(this.currentSearchCoordinates);
      }
    },
    outOfStockProductSku(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchInitialRetailers(this.currentSearchCoordinates);
      }
    },
  },
  mounted() {
    if (Object.keys(this.geolocation).length) {
      this.fetchInitialRetailers({latitude: this.geolocation.latitude, longitude: this.geolocation.longitude});
    }
    const options = {
      root: null,
      rootMargin: '0px',
    };
    this.observer = new IntersectionObserver(this.handleLoadMore, options);
    this.observer.observe(this.$refs.loadMoreTarget.$el);
  },
  unmounted() {
    this.observer.disconnect();
  },
  methods: {
    ...mapMutations('user', ['setSelectedRetailer']),
    ...mapActions('user', ['setSelectedLocationFromCoordinates']),
    ...mapActions('productDetails', ['setlocallyStockAvailableRetailers']),
    handleRetailerSelected(retailer) {
      this.setSelectedRetailer(retailer);
      if (this.coordinatesFromInput.length) {
        this.setSelectedLocationFromCoordinates({
          longitude: this.coordinatesFromInput[0],
          latitude: this.coordinatesFromInput[1],
        });
      }
      this.handleClose();
    },
    handleClose() {
      this.$emit('close');
    },
    async handleLoadMore(intersectionEvent) {
      if (!intersectionEvent[0]?.isIntersecting || !this.displayRetailerList.length) return;
      this.fetchMoreRetailers();
    },
    async fetchInitialRetailers({latitude, longitude}) {
      this.isLoading = true;
      try {
        const radius = convertPreferredUnitToMeters(RADIUS_IN_MILES, UnitTypes.IMPERIAL.milesString);
        this.currentPage = 0;
        const params = {
          radius,
          pageSize: PAGE_SIZE,
          currentPage: this.currentPage,
          latitude,
          longitude,
          ecommEnabled: true,
        };
        if (this.variantCode) {
          params.product = this.variantCode;
        }
        if (this.outOfStockProductSku) {
          params.product = this.outOfStockProductSku;
        }
        const {data: retailerData} = await RetailerApi.getRetailers(params);

        this.totalPages = retailerData.pagination?.totalPages;
        this.currentSearchCoordinates = {latitude, longitude};

        this.retailerList = retailerData.stores;
        this.setlocallyStockAvailableRetailers(this.retailerList?.filter((retailer) => retailer?.retailerStock));
      } catch (error) {
        this.retailerList = [];
      } finally {
        this.isLoading = false;
      }
    },
    async fetchMoreRetailers() {
      if (this.currentPage + 1 >= this.totalPages) return;
      this.currentPage = this.currentPage + 1;
      this.isLoadingMore = true;
      try {
        const radius = convertPreferredUnitToMeters(RADIUS_IN_MILES, UnitTypes.IMPERIAL.milesString);
        const params = {
          radius,
          pageSize: PAGE_SIZE,
          currentPage: this.currentPage,
          latitude: this.currentSearchCoordinates.latitude,
          longitude: this.currentSearchCoordinates.longitude,
          ecommEnabled: true,
        };
        if (this.variantCode) {
          params.product = this.variantCode;
        }
        const {data: retailerData} = await RetailerApi.getRetailers(params);

        this.retailerList = [...this.retailerList, ...retailerData.stores];
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoadingMore = false;
      }
    },
    handleLocationUpdate(event) {
      this.coordinatesFromInput = event;
    },
  },
};
</script>

<style lang="scss" scoped>
.pdl-drawer {
  z-index: 9997;

  @media (min-width: 640px) {
    top: var(--header-height);
    height: calc(100% - var(--header-height));
  }
}

:deep(.pdl-drawer__content) {
  background-color: var(--gray-05);
}
</style>
